import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import {
  MdOutlineDevices,
  MdOutlineShowChart,
  MdOutlineTune,
  MdOutlineVerifiedUser,
} from 'react-icons/md';

import { regulatorTier, type RegulatorTier } from '../data/reliability';
import { PlatformChip } from '../data/platforms';
import type { Broker } from '../data/types';
import { useDataText } from '../data/use-data-text';
import { CountryFlag } from './comparator-cells';
import { formatCompactUsd } from './comparator-utils';
import { DetailCard, DetailSection, SourcesFooter } from './detail-parts';

const TIER_CLASSES: Record<RegulatorTier, string> = {
  1: 'border-green-500/25 bg-green-500/10 text-green-300',
  2: 'border-sky-500/25 bg-sky-500/10 text-sky-300',
  3: 'border-white/10 bg-white/[0.04] text-neutral-400',
};

function RegulationSection({ broker }: { broker: Broker }) {
  const { t } = useTranslation('propfirms');
  const dataText = useDataText();
  return (
    <DetailSection title={t('detail.regulation')} icon={<MdOutlineVerifiedUser size={14} />}>
      {broker.regulation.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {broker.regulation.map((entry) => (
            <span
              key={entry}
              title={entry}
              className={cn(
                'rounded border px-1.5 py-0.5 text-[11px] font-medium',
                TIER_CLASSES[regulatorTier(entry)],
              )}
            >
              {entry}
            </span>
          ))}
        </div>
      ) : (
        <span className="text-[12px] text-neutral-500">{t('value.none')}</span>
      )}
      <div className="flex items-center gap-1.5 text-[11px] text-neutral-500">
        <span className="uppercase tracking-wide">{t('detail.jurisdiction')}</span>
        <CountryFlag headquarters={broker.headquarters} />
        <span className="text-neutral-300">
          {broker.headquarters ? dataText(broker.headquarters) : t('value.none')}
        </span>
      </div>
    </DetailSection>
  );
}

function ConditionsSection({ broker }: { broker: Broker }) {
  const { t } = useTranslation('propfirms');
  const dataText = useDataText();
  return (
    <DetailSection title={t('detail.conditions')} icon={<MdOutlineTune size={14} />}>
      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        <DetailCard label={t('detail.cond.minDeposit')}>
          {broker.minDepositUsd != null ? formatCompactUsd(broker.minDepositUsd) : t('value.none')}
        </DetailCard>
        <DetailCard label={t('detail.cond.spread')}>
          {broker.spreadFromPips != null
            ? t('broker.pips', { value: broker.spreadFromPips })
            : t('value.none')}
        </DetailCard>
        <DetailCard label={t('detail.cond.commission')}>
          {broker.commissionPerLotUsd != null ? `$${broker.commissionPerLotUsd}` : t('value.none')}
        </DetailCard>
        <DetailCard label={t('detail.cond.leverage')}>
          {dataText(broker.maxLeverage)}
        </DetailCard>
      </div>
    </DetailSection>
  );
}

export default function BrokerDetail({ broker }: { broker: Broker }) {
  const { t } = useTranslation('propfirms');
  const dataText = useDataText();
  return (
    <div className="flex flex-col gap-6">
      <RegulationSection broker={broker} />
      <ConditionsSection broker={broker} />
      <DetailSection title={t('detail.instruments')} icon={<MdOutlineShowChart size={14} />}>
        <div className="flex flex-wrap gap-1.5">
          {broker.instruments.map((instrument) => (
            <span
              key={instrument}
              className="rounded-full bg-white/[0.04] px-2 py-0.5 text-[11px] text-neutral-300"
            >
              {dataText(instrument)}
            </span>
          ))}
        </div>
      </DetailSection>
      <DetailSection title={t('detail.platforms')} icon={<MdOutlineDevices size={14} />}>
        <div className="flex flex-wrap gap-1.5">
          {broker.platforms.map((p) => (
            <PlatformChip key={p} platform={p} />
          ))}
        </div>
      </DetailSection>
      <SourcesFooter
        notes={dataText(broker.notes)}
        sources={broker.sources}
        lastVerified={broker.lastVerified}
      />
    </div>
  );
}
